import {
  AI_PANEL_MONITORING_CLIMATE_SELECTABLE_UNIT_TYPES,
  AI_PANEL_MONITORING_PRODUCT_SELECTABLE_UNIT_TYPES,
  AI_PANEL_MONITORING_SELECTABLE_UNIT_TYPES,
  AI_PANEL_SELECTABLE_UNIT_TYPES,
  VC_BOARD_TYPES,
} from 'constants/general';
import { VALUE_CHAIN_MAP_CUSTOM_NODES, VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';

import {
  isMonitoringAgentKind,
} from '../../../pages/CompanyAdminPages/CompanyMap/components/ValueChain/interfaces/TValueChainActiveAgentKind';
import { getBoardForSwimlane } from '../../../pages/CompanyAdminPages/CompanyMap/components/ValueChain/utils/valueChainSectionValidation';

import type { Node } from '@xyflow/react';
import type { TMonitoringSelectionMode } from 'constants/interfaces/IMonitoringAgentDefinition';
import type { TValueChainActiveAgentKind } from '../../../pages/CompanyAdminPages/CompanyMap/components/ValueChain/interfaces/TValueChainActiveAgentKind';

export const getBoardNameBySwimlaneId = (
  swimlaneId: string,
  nodes: Node[],
): string | undefined => {
  if (!swimlaneId) {
    return undefined;
  }

  const swimlaneNode = nodes.find(
    (node) => node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE && node.id === swimlaneId,
  );

  if (!swimlaneNode) {
    return undefined;
  }

  const boardNode = getBoardForSwimlane(nodes, swimlaneNode);
  return typeof boardNode?.data?.name === 'string' ? boardNode.data.name : undefined;
};

export const isUnitSelectableForMonitoring = (unitType: string): boolean => (
  AI_PANEL_MONITORING_SELECTABLE_UNIT_TYPES.has(unitType)
);

export const isUnitSelectableForMonitoringProducts = (unitType: string): boolean => (
  AI_PANEL_MONITORING_PRODUCT_SELECTABLE_UNIT_TYPES.has(unitType)
);

export const isUnitSelectableForClimateMonitoring = (unitType: string): boolean => (
  AI_PANEL_MONITORING_CLIMATE_SELECTABLE_UNIT_TYPES.has(unitType)
);

export const isSelectableMonitoringSupplier = (
  unitType: string,
  swimlaneId: string,
  nodes: Node[],
): boolean => (
  isUnitSelectableForMonitoring(unitType)
  && Boolean(swimlaneId)
  && getBoardNameBySwimlaneId(swimlaneId, nodes) === VC_BOARD_TYPES.UPSTREAM
);

export const isSelectableMonitoringProduct = (
  unitType: string,
  swimlaneId: string,
  nodes: Node[],
): boolean => {
  if (!isUnitSelectableForMonitoringProducts(unitType) || !swimlaneId) {
    return false;
  }

  const boardName = getBoardNameBySwimlaneId(swimlaneId, nodes);

  if (unitType === VALUE_CHAIN_UNIT_TYPES.PRODUCT) {
    return boardName === VC_BOARD_TYPES.UPSTREAM;
  }

  if (unitType === VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT) {
    return boardName === VC_BOARD_TYPES.OWN_OPERATIONS;
  }

  return false;
};

export const isSelectableClimateEntity = (
  unitType: string,
  swimlaneId: string,
  nodes: Node[],
): boolean => {
  if (!isUnitSelectableForClimateMonitoring(unitType) || !swimlaneId) {
    return false;
  }

  const boardName = getBoardNameBySwimlaneId(swimlaneId, nodes);

  if (unitType === VALUE_CHAIN_UNIT_TYPES.SUPPLIER) {
    return boardName === VC_BOARD_TYPES.UPSTREAM;
  }

  if (
    unitType === VALUE_CHAIN_UNIT_TYPES.OWN_OPERATION
    || unitType === VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET
  ) {
    return boardName === VC_BOARD_TYPES.OWN_OPERATIONS;
  }

  return false;
};

export const isSelectableForMonitoringSelectionMode = (
  selectionMode: TMonitoringSelectionMode | undefined,
  unitType: string,
  swimlaneId: string,
  nodes: Node[],
): boolean => {
  if (selectionMode === 'products-mixed') {
    return isSelectableMonitoringProduct(unitType, swimlaneId, nodes);
  }

  if (selectionMode === 'climate-mixed') {
    return isSelectableClimateEntity(unitType, swimlaneId, nodes);
  }

  if (selectionMode === 'upstream-suppliers') {
    return isSelectableMonitoringSupplier(unitType, swimlaneId, nodes);
  }

  return false;
};

export const resolveAIPanelSelectableUnitTypes = (
  activeAgentKind: TValueChainActiveAgentKind,
): ReadonlySet<string> => {
  if (activeAgentKind === 'regulation-monitoring') {
    return AI_PANEL_MONITORING_PRODUCT_SELECTABLE_UNIT_TYPES;
  }

  if (activeAgentKind === 'climate-monitoring') {
    return AI_PANEL_MONITORING_CLIMATE_SELECTABLE_UNIT_TYPES;
  }

  if (
    activeAgentKind === 'reputation-monitoring'
    || activeAgentKind === 'finance-monitoring'
    || activeAgentKind === 'ownership-monitoring'
  ) {
    return AI_PANEL_MONITORING_SELECTABLE_UNIT_TYPES;
  }

  return AI_PANEL_SELECTABLE_UNIT_TYPES;
};

export const isUnitSelectableForAIPanel = (
  unitType: string,
  selectableUnitTypes: ReadonlySet<string>,
): boolean => selectableUnitTypes.has(unitType);

export const isSupplierUnitType = (unitType: string): boolean => (
  unitType === VALUE_CHAIN_UNIT_TYPES.SUPPLIER
);

export const isActiveMonitoringSelection = (
  activeAgentKind: TValueChainActiveAgentKind,
): boolean => isMonitoringAgentKind(activeAgentKind);
